import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  MenuItem,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  FormHelperText
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Description as FileIcon,
  CheckCircle as SuccessIcon,
  CheckCircle,
  Cancel as ErrorIcon
} from '@mui/icons-material';
import { documentApi } from '../services/api';
import api from '../services/api';
import PageHeader from '../components/PageHeader';

// 文件类型与图标颜色映射
const fileTypeColors: Record<string, string> = {
  'application/pdf': '#f44336',
  'text/plain': '#2196f3',
  'application/msword': '#ff9800',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '#ff9800',
  'application/vnd.ms-excel': '#4caf50',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '#4caf50',
  'application/vnd.ms-powerpoint': '#9c27b0',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': '#9c27b0',
  'image/jpeg': '#e91e63',
  'image/png': '#e91e63',
  'text/html': '#00bcd4',
  'application/json': '#607d8b',
  'application/xml': '#009688',
  'text/csv': '#8bc34a'
};

// 文件大小格式化
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const DocumentUpload: React.FC = () => {
  const navigate = useNavigate();
  const [title, setTitle] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [category, setCategory] = useState<string>('');
  const [categoryError, setCategoryError] = useState<string>('');
  const [uploadedBy, setUploadedBy] = useState<string>('');
  const [file, setFile] = useState<File | null>(null);
  const [files, setFiles] = useState<FileList | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [titleError, setTitleError] = useState<string>('');
  const [uploadedByError, setUploadedByError] = useState<string>('');
  const [fileError, setFileError] = useState<string>('');
  const [departments, setDepartments] = useState<Array<{id: number, name: string}>>([]);
  const [userDepartments, setUserDepartments] = useState<string[]>([]);
  const [userRole, setUserRole] = useState<string>('');
  const [departmentsLoading, setDepartmentsLoading] = useState<boolean>(false);

  // 获取当前登录用户信息
  React.useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        console.log('DocumentUpload - 用户信息:', user);
        setUploadedBy(user.username || '');
        setUserRole(user.role || '');
        setUserDepartments(user.departments || []);
        console.log('DocumentUpload - 设置用户角色:', user.role);
        console.log('DocumentUpload - 设置用户部门:', user.departments);
      } catch (error) {
        console.error('解析用户信息失败:', error);
      }
    }
  }, []);

  // 监听用户角色变化，如果是管理员则加载部门数据
  React.useEffect(() => {
    if (userRole === 'ADMIN' || userRole === 'SUPER_ADMIN') {
      console.log('DocumentUpload - 用户角色为管理员，开始加载部门数据');
      const loadDepartments = async () => {
        try {
          setDepartmentsLoading(true);
          const response = await api.get('/api/departments');
          console.log('DocumentUpload - 获取到的部门数据:', response.data);
          setDepartments(response.data);
          setDepartmentsLoading(false);
        } catch (error) {
          console.error('加载部门列表失败:', error);
          setDepartmentsLoading(false);
        }
      };
      loadDepartments();
    }
  }, [userRole]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const selectedFile = event.target.files[0];
      setFile(selectedFile);
      setFileError('');
      
      // 自动设置文档标题为文件名（去掉扩展名）
      const fileName = selectedFile.name;
      const fileNameWithoutExt = fileName.replace(/\.[^/.]+$/, ""); // 移除文件扩展名
      setTitle(fileNameWithoutExt);
      
      // 自动设置文档描述（和标题一样）
      setDescription(fileNameWithoutExt);
    }
  };

  const handleFilesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files;
    setFiles(selected);
    if (selected && selected.length > 0) {
      const first = selected[0];
      setTitle(first.name);
      setDescription(first.name);
    }
  };

  const validateForm = (): boolean => {
    let isValid = true;

    // 验证标题
    if (!title.trim()) {
      setTitleError('标题不能为空');
      isValid = false;
    } else {
      setTitleError('');
    }

    // 验证分类
    if (!category.trim()) {
      setCategoryError('请选择分类');
      isValid = false;
    } else {
      setCategoryError('');
    }

    // 验证上传者
    if (!uploadedBy.trim()) {
      setUploadedByError('无法获取当前用户信息，请重新登录');
      isValid = false;
    } else {
      setUploadedByError('');
    }

    // 验证文件
    if (!file && (!files || files.length === 0)) {
      setFileError('请选择要上传的文件');
      isValid = false;
    } else {
      setFileError('');
    }

    return isValid;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      if (files && files.length >= 1) {
        const formData = new FormData();
        // 多文件字段名为 files[] 或 files，后端接受 MultipartFile[] files
        for (let i = 0; i < files.length; i++) {
          formData.append('files', files[i]);
        }
        formData.append('uploadedBy', uploadedBy);
        if (category) formData.append('category', category);
        if (category && category.match(/^\d+$/)) {
          // 如果分类是部门ID，额外传 departmentId
          formData.append('departmentId', category);
        }

        const created = await documentApi.uploadDocumentsBatch(formData);

        // 批量索引所有上传的文档
        console.log(`批量上传成功，开始索引 ${created.length} 个文档...`);
        for (const doc of created) {
          try {
            console.log(`正在索引文档: ${doc.title} (ID: ${doc.id})`);
            await documentApi.processAndIndexDocument(doc.id);
            console.log(`文档索引完成: ${doc.title}`);
          } catch (indexErr) {
            console.error(`文档 ${doc.title} 索引过程出错:`, indexErr);
          }
        }
        console.log('批量索引完成');

        // 成功后跳转到列表或第一条详情
        setSuccess(true);
        setLoading(false);
        setTimeout(() => {
          if (created && created.length > 0) {
            navigate(`/documents/${created[0].id}`);
          } else {
            navigate('/documents');
          }
        }, 3000);
        return;
      }

      // 兼容原单文件上传路径
      if (file) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('title', title);
        formData.append('description', description || '');
        formData.append('category', category || '');
        formData.append('uploadedBy', uploadedBy);
        const response = await documentApi.uploadDocument(formData);
        try {
          await documentApi.processAndIndexDocument(response.id);
        } catch (indexErr) {
          console.error('文档索引过程出错:', indexErr);
        }
        setSuccess(true);
        setLoading(false);
        setTimeout(() => {
          navigate(`/documents/${response.id}`);
        }, 3000);
      }
    } catch (err) {
      console.error('文档上传失败:', err);
      setError('文档上传失败，请重试');
      setLoading(false);
      setSuccess(false);
    }
  };

  return (
    <Box className="page-container">
      <PageHeader
        title="上传文档"
        subtitle="支持单文件与批量上传，自动解析并入库"
        breadcrumbs={[{ label: '知识库', to: '/documents' }, { label: '上传文档' }]}
      />

      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            {success ? (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <SuccessIcon color="success" sx={{ fontSize: 60, mb: 2 }} />
                <Typography variant="h5" gutterBottom>
                  文档上传成功！
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  正在跳转到文档详情页面...
                </Typography>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <form onSubmit={handleSubmit}>
                {error && (
                  <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                  </Alert>
                )}

                {/* 文件选择 - 放在第一行 */}
                <Box sx={{ 
                  mb: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1
                }}>
                  <input
                    id="file-upload"
                    type="file"
                    accept=".pdf,.doc,.docx,.txt,.md,.ppt,.pptx,.xls,.xlsx,.png,.jpg,.jpeg"
                    multiple
                    onChange={handleFilesChange}
                  />
                  <label htmlFor="file-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<UploadIcon />}
                      sx={{ 
                        width: '200px',
                        height: '40px',
                        fontSize: '14px',
                        borderRadius: '6px',
                        borderColor: '#1976d2',
                        color: '#1976d2',
                        '&:hover': {
                          borderColor: '#1565c0',
                          backgroundColor: 'rgba(25, 118, 210, 0.04)'
                        }
                      }}
                    >
                      选择文件（可多选）
                    </Button>
                  </label>
                  {fileError && (
                    <FormHelperText error sx={{ textAlign: 'center' }}>{fileError}</FormHelperText>
                  )}
                </Box>

                <Box sx={{ mb: 3 }}>
                  <TextField
                    label="文档标题"
                    variant="outlined"
                    fullWidth
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    error={!!titleError}
                    helperText={titleError || "文件名将自动填充为标题"}
                    required
                  />
                </Box>

                <Box sx={{ mb: 3 }}>
                  <TextField
                    label="文档描述"
                    variant="outlined"
                    fullWidth
                    multiline
                    rows={3}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    helperText="文件名将自动填充为描述"
                  />
                </Box>

                <Box sx={{ mb: 3 }}>
                  <FormControl fullWidth variant="outlined" error={!!categoryError}>
                    <InputLabel id="category-label">部门分类 *</InputLabel>
                    <Select
                      labelId="category-label"
                      value={category}
                      onChange={(e: SelectChangeEvent<string>) => {
                        console.log('上传页面分类选择变化:', e.target.value);
                        setCategory(e.target.value);
                        setCategoryError('');
                      }}
                      label="部门分类 *"
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 300
                          }
                        }
                      }}
                    >
                      {userRole === 'ADMIN' || userRole === 'SUPER_ADMIN' ? (
                        departmentsLoading ? (
                          <MenuItem disabled>加载中...</MenuItem>
                        ) : departments.length === 0 ? (
                          <MenuItem disabled>暂无部门数据</MenuItem>
                        ) : (
                          // 管理员和超级管理员显示所有部门，使用与普通用户相同的格式
                          departments.map((dept) => (
                            <MenuItem 
                              key={`upload-admin-dept-${dept.name}`} 
                              value={dept.name}
                            >
                              {dept.name}
                            </MenuItem>
                          ))
                        )
                      ) : (
                        // 普通用户只显示自己所属的部门，不显示"其他"选项
                        userDepartments.map((deptName) => (
                          <MenuItem key={`upload-user-dept-${deptName}`} value={deptName}>
                            {deptName}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                    {categoryError && (
                      <FormHelperText error>{categoryError}</FormHelperText>
                    )}
                  </FormControl>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <TextField
                    label="上传者"
                    variant="outlined"
                    fullWidth
                    value={uploadedBy}
                    onChange={(e) => setUploadedBy(e.target.value)}
                    error={!!uploadedByError}
                    helperText={uploadedByError || "当前登录用户"}
                    required
                    disabled
                  />
                </Box>

                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  mt: 4,
                  mb: 2
                }}>
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} /> : null}
                    sx={{
                      width: '300px',
                      height: '48px',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      borderRadius: '8px',
                      boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                      '&:hover': {
                        boxShadow: '0 6px 12px rgba(0,0,0,0.15)',
                        transform: 'translateY(-1px)'
                      },
                      transition: 'all 0.2s ease-in-out'
                    }}
                  >
                    {loading ? '上传中...' : '上传文档'}
                  </Button>
                </Box>
              </form>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={5}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                已选文件
              </Typography>
              {files && files.length > 0 ? (
                <List>
                  {Array.from(files).map((f, idx) => (
                    <ListItem key={`selected-file-${idx}`}>
                      <ListItemIcon>
                        <FileIcon style={{ color: fileTypeColors[f.type] || '#757575' }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={f.name}
                        secondary={
                          <>
                            {f.type || '未知类型'} - {formatFileSize(f.size)}
                          </>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              ) : file ? (
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <FileIcon style={{ color: fileTypeColors[file.type] || '#757575' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={file.name}
                      secondary={
                        <>
                          {file.type || '未知类型'} - {formatFileSize(file.size)}
                        </>
                      }
                    />
                  </ListItem>
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  请选择一个或多个文件进行上传
                </Typography>
              )}
            </CardContent>
          </Card>

          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                上传说明
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle color="success" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="支持多种文档格式，包括PDF、Word、Excel、文本文件等" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle color="success" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="文件大小限制为50MB" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle color="success" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="上传后系统会自动提取文档内容并进行索引" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle color="success" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="添加分类和详细描述有助于更好地管理和查找文档" />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DocumentUpload; 